import React, { createContext, useContext, useMemo, useState } from 'react'

/**
 * Shared skills-matrix category filter. Lets other components (the
 * architecture diagram) pre-filter the matrix — e.g. selecting the AKS
 * node filters skills to 'Infrastructure & DevOps' — without scrolling
 * or stealing focus.
 */
type SkillsFilterContextType = {
	category: string
	setCategory: (category: string) => void
	/**
	 * True while an architecture node is hovered/tapped — the skills
	 * matrix section opens temporarily so the filtered result is visible
	 * without an extra click. If the visitor had it closed, it closes
	 * again when the hover ends; if they had it open, it stays open.
	 */
	matrixAutoOpen: boolean
	setMatrixAutoOpen: (open: boolean) => void
}

const SkillsFilterContext = createContext<SkillsFilterContextType | undefined>(undefined)

export const SkillsFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [category, setCategory] = useState<string>('all')
	const [matrixAutoOpen, setMatrixAutoOpen] = useState(false)

	const value = useMemo(
		() => ({ category, setCategory, matrixAutoOpen, setMatrixAutoOpen }),
		[category, matrixAutoOpen]
	)

	return <SkillsFilterContext.Provider value={value}>{children}</SkillsFilterContext.Provider>
}

export const useSkillsFilter = (): SkillsFilterContextType => {
	const context = useContext(SkillsFilterContext)
	if (context === undefined) {
		throw new Error('useSkillsFilter must be used within a SkillsFilterProvider')
	}
	return context
}
